import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import usePropHelper from "../../../hooks/PropHelper";
import { IAttach } from "../../../models/interactions/Attach";
import Interaction from "../../../models/interactions/Interaction";
import { Prop } from "../../../models/Prop";
import NodeActorDropdown from "../components/NodeActorDropdown";
import NodeAttachPropDropdown from "../components/NodeAttachPropDropdown";
import NodeAttachTagDropdown from "../components/NodeAttachTagDropdown";
import NodeAttachTypeDropdown from "../components/NodeAttachTypeDropdown";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import { applyPickedPropToInteraction } from "./SequencePropPicker.helpers";

export default function AttachNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const attach = props.interaction as IAttach;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const { getUsedPropByInstanceId } = usePropHelper();
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "prop";

  const onChangePropDropdown = (prop: Prop) => {
    updateInteraction(props.nodeId, {
      ...applyPickedPropToInteraction(attach, prop, getUsedPropByInstanceId),
    } as IAttach);
  };

  const actorDropdown = (
    <NodeActorDropdown
      label="Actor"
      selectedActorId={attach.playerGuid}
      onClick={(actorId: string) =>
        updateInteraction(props.nodeId, {
          ...attach,
          playerGuid: actorId,
        } as IAttach)
      }
    />
  );

  const propDropdown = (
    <NodeAttachPropDropdown
      selectedPropId={attach.propGuid}
      onChange={onChangePropDropdown}
      accessory={
        <button
          type="button"
          aria-label="Pick on map"
          title="Pick on map"
          data-testid="pick-prop-on-map-button"
          className={
            "group flex h-[32px] w-[32px] items-center justify-center rounded-full border-[2px] transition-all duration-150 " +
            (isPickerActiveForNode
              ? "border-creator-primary bg-creator-primary text-white shadow-[0_0_0_4px_rgba(58,196,255,0.18)]"
              : "border-creator-primary bg-creator-primary-background text-creator-primary hover:bg-creator-primary-washed")
          }
          onClick={() =>
            dispatch(
              openSequencePositionPicker({
                nodeId: props.nodeId,
                positionType: "prop",
                title: "Pick prop",
                presentation: "splitView",
              }),
            )
          }
        >
          <ColorizeIcon fontSize="inherit" className="text-[18px]" />
        </button>
      }
    />
  );

  const tagDropdown = (
    <NodeAttachTagDropdown
      selectedTag={attach.tag}
      selectedProp={attach.propGuid}
      onChange={(tag) =>
        updateInteraction(props.nodeId, { ...attach, tag } as IAttach)
      }
    />
  );

  const typeDropdown = (
    <NodeAttachTypeDropdown
      playerToProp={attach.playerToProp}
      onChange={(playerToProp) =>
        updateInteraction(props.nodeId, { ...attach, playerToProp } as IAttach)
      }
    />
  );

  return (
    <div className="w-full h-full flex flex-col gap-[4px]">
      {actorDropdown}
      {propDropdown}
      {tagDropdown}
      {typeDropdown}
    </div>
  );
}
